import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth.js";
import { useParams } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions.js"; // Function to fetch user data
import {
  getOrgById,
  getOrgByRef,
  transformOrgsToTable,
} from "../../models/OrgFunctions.js"; // Functions to fetch org and client data
import Column from "../../layout/Column.js";
import Row from "../../layout/Row.js";
import Divider from "../../layout/Divider.js";
import Space from "../../layout/Space.js";
import DataGridFull from "../../tables/DataGridFull.js";
import { useTheme } from "@mui/material";
import Icon1 from "../../icons/Icon.js";
import {
  getFlutterTime,
  getFlutterTimeFromSeconds,
  queryCollection,
} from "../../models/DataFunctions.js";
import TFGPT1 from "../../textfields/TFGPT1.js";
import DropDownGPT0 from "../../dropdowns/DropDownGTP0.js";
import { EasySwitch } from "../../textfields/EasySwitch.js";
import DataGridGPT2 from "../../tables/DataGridGPT2.js";
import DropDownGPT03 from "../../dropdowns/DropDownGTP03.js";
import { filter, folder } from "jszip";

function Orgs({ navigateWorkspace }) {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();
  const [ca, setCa] = useState(0);
  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [remove_fake, setRemoveFake] = useState(true);
  const [searchValue, setSearchValue] = useState(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);
  const [filter_type, setFilterType] = useState("master");
  const [this_month_folders, setThisMonthFolders] = useState([]);
  const [orgs, setOrgs] = useState([]);
  // Function to navigate to a client by ID
  const gotoOrg = (org) => {
    navigateWorkspace(`/admin/org/${org.id}`);
  };

  // Function to navigate to a client by name
  const gotoOrgEmail = (value) => {
    if (!value) return;
    // Find the client with the matching name
    const org = orgs.find((row) => row.email === value.value);

    // If client exists, navigate to the client page by ID
    if (org) {
      navigateWorkspace(`/admin/org/${org.id}`);
    } else {
      console.error("Org not found");
    }
  };

  useEffect(() => {
    let filteredOrgs = orgs.filter((org) => {
      if (remove_fake) {
        return org.facturation?.fake_account !== true;
      }
      return true;
    });

    const { rows, columns } = transformOrgsToTable(
      filteredOrgs,
      this_month_folders,
      filter_type
    );
    setRows(rows);
    setColumns(columns);
  }, [orgs, filter_type, remove_fake]);

  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (currentUser) {
      // Fetch user data by currentUser's ID
      const user = await getUserById(currentUser.uid);

      // Fetch organization data using the user's org reference
      const org = await getOrgById(user.org_id);

      // Fetch clients associated with the user's organization
      const orgs = await queryCollection("org", []);

      const this_month_folder = await queryCollection("folder", [
        {
          field: "created_time",
          operator: ">=",
          value: getFlutterTimeFromSeconds(
            new Date().getTime() / 1000 - 30 * 24 * 60 * 60
          ),
        },
      ]);
      setThisMonthFolders(this_month_folder);
      console.log("This month folders:", this_month_folder);

      // Transform clients into table format (rows and columns)
      const { rows, columns } = transformOrgsToTable(
        orgs,
        this_month_folder || [],
        filter_type
      );
      let ca = orgs
        .filter((org) => {
          if (remove_fake) {
            return org.facturation?.fake_account !== true;
          }
          return true;
        })
        .reduce((acc, org) => {
          if (org?.facturation?.LTV) {
            let ltv_number = parseFloat(org.facturation?.LTV) || 0;
            return acc + ltv_number || 0;
          }
          return acc;
        }, 0);
      setCa(ca);
      // Update state if the user data is new or changed
      if (user && user !== userData) {
        setUserData(user);
        setOrgData(org);
        setOrgs(orgs);
        setRows(rows);
        setColumns(columns);

        // Set options for the search bar (client names)
        setOptions(
          orgs.map((org) => ({
            value: org.email,
            label: org.email + " - " + org.name,
          }))
        );
      }
    }
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser, filter_type]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData) {
    return <div></div>;
  }

  // Render the client data
  return (
    <div className="flex flex-col h-full w-full pl-4 ">
      <div className="flex flex-row w-full h-full">
        <div className="flex flex-col gap-2 h-full w-full overflow-hidden">
          <div className="text-hx font-medium">Organisations</div>
          <div className="bg-white border-1 border-shadow_25 rounded-full max-w-c500 flex flex-row gap-2 items-center px-2">
            <Icon1 icon_name="search" size={20} fill="grey"></Icon1>
            <TFGPT1
              options={options}
              placeholder="Chercher"
              inputValue={searchValue}
              setInputValue={setSearchValue}
              onAction={gotoOrgEmail}
              className="w-full"
              start_only={false}
            ></TFGPT1>
          </div>{" "}
          <div className="flex flex-row items-center gap-2 w-full">
            <DropDownGPT03
              className={`w-40`}
              items={[
                {
                  value: null,
                  label: "All",
                },
                {
                  value: "master",
                  label: "Master",
                },
                {
                  value: "client",
                  label: "Client",
                },
                {
                  value: "expert",
                  label: "Expert",
                },
              ]}
              label={"Filter by type"}
              value={filter_type}
              onChange={(e) => setFilterType(e.value)}
            ></DropDownGPT03>
            <EasySwitch
              checked={remove_fake}
              onChange={(value) => setRemoveFake(value)}
            ></EasySwitch>
            <div className="text-h3 ">Remove fake</div>{" "}
            <div className="text-h3 text-shadow_50 cursor-pointer">
              Ca : {ca} €
            </div>
          </div>
          <Space y={0} />
          {/* DataGrid displaying the clients */}
          <div
            style={{ padding: "0px" }}
            className="w-full h-full max-w-c1p4k overflow-y-auto mb-6 pb-6"
          >
            <div className="h-full min-h-6 pb-6">
              <DataGridGPT2
                rows={rows}
                columns={columns}
                onClick={gotoOrg}
                initial_sort_field="creation"
                initial_asc="desc"
              />
            </div>
          </div>
        </div>

        <Divider orientation="vertical" />
      </div>
    </div>
  );
}

export default Orgs;
